import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
} from 'react';
import type { WorkItem } from '../../config/work';
import { Reveal } from '../ui/Reveal';
import { WorkCard } from './WorkCard';

interface WorkCarouselProps {
  items: readonly WorkItem[];
  ariaLabel: string;
}

interface DragState {
  pointerId: number;
  startX: number;
  startScrollLeft: number;
  moved: boolean;
}

const DRAG_THRESHOLD_PX = 8;
const SCROLL_SETTLE_MS = 90;

function reducedMotionRequested() {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function WorkCarousel({ items, ariaLabel }: WorkCarouselProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const primaryRef = useRef<HTMLDivElement>(null);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const suppressNextClickRef = useRef(false);
  const [dragging, setDragging] = useState(false);

  const normalizePosition = useCallback(() => {
    const viewport = viewportRef.current;
    const primary = primaryRef.current;
    if (!viewport || !primary || primary.offsetWidth === 0) return;

    const segmentWidth = primary.offsetWidth;
    const primaryStart = primary.offsetLeft;
    const viewportCenter = viewport.scrollLeft + viewport.clientWidth / 2;

    if (viewportCenter < primaryStart) {
      viewport.scrollLeft += segmentWidth;
    } else if (viewportCenter >= primaryStart + segmentWidth) {
      viewport.scrollLeft -= segmentWidth;
    }
  }, []);

  const scheduleNormalize = useCallback(() => {
    if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    settleTimerRef.current = setTimeout(normalizePosition, SCROLL_SETTLE_MS);
  }, [normalizePosition]);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const primary = primaryRef.current;
    if (viewport && primary) viewport.scrollLeft = primary.offsetLeft;
  }, [items]);

  useEffect(() => () => {
    if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
  }, []);

  const scrollByOne = useCallback((direction: -1 | 1) => {
    const viewport = viewportRef.current;
    const primary = primaryRef.current;
    const firstSlide = primary?.querySelector<HTMLElement>('[data-carousel-slide]');
    if (!viewport || !primary || !firstSlide) return;

    const styles = window.getComputedStyle(primary);
    const parsedGap = Number.parseFloat(styles.columnGap || styles.gap || '0');
    const gap = Number.isFinite(parsedGap) ? parsedGap : 0;
    const step = firstSlide.offsetWidth + gap;

    viewport.scrollBy({
      left: direction * step,
      behavior: reducedMotionRequested() ? 'auto' : 'smooth',
    });
    scheduleNormalize();
  }, [scheduleNormalize]);

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startScrollLeft: event.currentTarget.scrollLeft,
      moved: false,
    };
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    const delta = event.clientX - drag.startX;
    if (!drag.moved && Math.abs(delta) < DRAG_THRESHOLD_PX) return;

    if (!drag.moved) {
      drag.moved = true;
      setDragging(true);
      event.currentTarget.setPointerCapture?.(event.pointerId);
    }

    event.preventDefault();
    event.currentTarget.scrollLeft = drag.startScrollLeft - delta;
  };

  const finishPointerDrag = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    if (drag.moved) {
      suppressNextClickRef.current = true;
      setTimeout(() => {
        suppressNextClickRef.current = false;
      }, 0);
    }

    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
    dragRef.current = null;
    setDragging(false);
    scheduleNormalize();
  };

  const handleClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    if (!suppressNextClickRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    suppressNextClickRef.current = false;
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      scrollByOne(-1);
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      scrollByOne(1);
    }
  };

  if (items.length === 0) return null;

  if (items.length === 1) {
    return (
      <div className="work-carousel work-carousel--single" role="region" aria-label={ariaLabel}>
        <div className="work-carousel__single" data-testid="work-carousel-primary">
          <WorkCard item={items[0]} />
        </div>
      </div>
    );
  }

  const renderSegment = (copy: 'before' | 'primary' | 'after') => {
    const isPrimary = copy === 'primary';
    return (
      <div
        className={`work-carousel__segment work-carousel__segment--${copy}`}
        ref={isPrimary ? primaryRef : undefined}
        aria-hidden={isPrimary ? undefined : true}
        inert={isPrimary ? undefined : true}
        data-testid={isPrimary ? 'work-carousel-primary' : undefined}
      >
        {items.map((item) => (
          <div className="work-carousel__slide" data-carousel-slide key={`${copy}-${item.slug}`}>
            <Reveal className="work-carousel__slide-reveal">
              <WorkCard item={item} />
            </Reveal>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="work-carousel" role="region" aria-roledescription="carousel" aria-label={ariaLabel}>
      <div className="work-carousel__controls" aria-label="Carousel controls">
        <button type="button" className="work-carousel__control" onClick={() => scrollByOne(-1)} aria-label="Previous website concept">
          <ChevronLeft size={20} aria-hidden="true" />
        </button>
        <button type="button" className="work-carousel__control" onClick={() => scrollByOne(1)} aria-label="Next website concept">
          <ChevronRight size={20} aria-hidden="true" />
        </button>
      </div>
      <div
        ref={viewportRef}
        className={`work-carousel__viewport${dragging ? ' work-carousel__viewport--dragging is-dragging' : ''}`}
        tabIndex={0}
        onScroll={scheduleNormalize}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishPointerDrag}
        onPointerCancel={finishPointerDrag}
        onClickCapture={handleClickCapture}
        onKeyDown={handleKeyDown}
        onDragStart={(event) => event.preventDefault()}
      >
        {renderSegment('before')}
        {renderSegment('primary')}
        {renderSegment('after')}
      </div>
    </div>
  );
}
